import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";

interface ShoppingSession {
  id: string;
  category: string;
  budgetLowCents: number;
  budgetHighCents: number;
  quotedFeeCents: number;
  feeWaived: boolean;
  status: string;
  openedAt: string;
  expiresAt: string;
  clickedThroughAt: string | null;
}

const usd = (cents: number) => `$${(cents / 100).toFixed(0)}`;

function formatDate(iso: string): string {
  // SQLite's datetime() has no timezone marker; treat it as UTC rather than
  // letting the browser read it as local and drift the date.
  const normalized = iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`;
  return new Date(normalized).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ShoppingSession[] | null>(null);

  useEffect(() => {
    fetch("/api/sessions", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { sessions: [] }))
      .then((data) => setSessions((data as { sessions?: ShoppingSession[] }).sessions ?? []))
      .catch(() => setSessions([]));
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back, {user?.name}!</p>
        </div>
        <Button asChild>
          <Link to="/search">Start a search</Link>
        </Button>
      </div>

      {sessions !== null && sessions.length === 0 && (
        <EmptyState
          welly
          title="No shopping sessions yet"
          description="Start a search and we'll work the whole catalog for one category, then show you the few that survived — and why the rest didn't."
          action={{ label: "Start a search", onClick: () => navigate("/search") }}
        />
      )}

      {sessions !== null && sessions.length > 0 && (
        <div className="space-y-4">
          {sessions.map((session) => (
            <Card key={session.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg">{session.category}</CardTitle>
                    <CardDescription>
                      {usd(session.budgetLowCents)}–{usd(session.budgetHighCents)} ·{" "}
                      {formatDate(session.openedAt)}
                    </CardDescription>
                  </div>
                  <span className="label text-xs text-muted-foreground">
                    {session.status === "open"
                      ? `open until ${formatDate(session.expiresAt)}`
                      : session.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {session.feeWaived
                    ? `Would have cost $${(session.quotedFeeCents / 100).toFixed(2)} — waived while Pickwell is free.`
                    : `Fee: $${(session.quotedFeeCents / 100).toFixed(2)}`}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
